// NavSearch — full-screen command palette for jumping to any page in
// the app. Triggered from the sidebar header Search icon. All filtering
// is client-side over a hardcoded grouped link list — no server calls.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NavSearch.module.css';

interface NavLink {
  label: string;
  to: string;
}

interface NavGroup {
  group: string;
  items: NavLink[];
}

const LINKS: NavGroup[] = [
  {
    group: 'Navigation',
    items: [
      { label: 'Dashboard', to: '/collections/dashboard' },
      { label: 'Diary', to: '/collections/diary' },
      { label: 'Bookings', to: '/collections/bookings' },
      { label: 'Pendings', to: '/collections/pending' },
      { label: 'Customers', to: '/customers' },
      { label: 'Cancellations', to: '/collections/cancellations' },
      { label: 'GeoLink', to: '/collections/geolink' },
      { label: 'Receipts', to: '/collections/receipts' },
      { label: 'Invoices', to: '/collections/invoices' },
      { label: 'Payment Run', to: '/collections/payment-run' },
      { label: 'Reports', to: '/reports' },
      { label: 'Weighbridge Reports', to: '/reports/weighbridge' },
      { label: 'Integrations', to: '/collections/integrations' },
      { label: 'Skip Dashboard', to: '/skip/dashboard' },
      { label: 'Skip Jobs', to: '/skip/jobs' },
      { label: 'Skip Enquiries', to: '/skip/enquiries' },
      { label: 'Skip Customers', to: '/skip/customers' },
      { label: 'Skip Driver Map', to: '/skip/drivers/map' },
      { label: 'Weighbridge Dashboard', to: '/weighbridge/dashboard' },
      { label: 'Weighbridge Tickets', to: '/weighbridge/tickets' },
      { label: 'Weighbridge Customers', to: '/weighbridge/customers' },
    ],
  },
  {
    group: 'Admin',
    items: [
      { label: 'Admin', to: '/admin' },
      { label: 'Users', to: '/admin/users' },
      { label: 'Invite User', to: '/admin/users/invite' },
      { label: 'Drivers', to: '/admin/drivers' },
      { label: 'Add Driver', to: '/admin/drivers/create' },
      { label: 'Driver Devices', to: '/admin/drivers/devices' },
      { label: 'Roles', to: '/admin/roles' },
      { label: 'Metrics', to: '/reports/collections/metrics' },
      { label: 'Audit Log', to: '/reports/collections/audit-log' },
    ],
  },
  {
    group: 'Settings',
    items: [
      { label: 'Settings', to: '/settings' },
      { label: 'Company Settings', to: '/settings/company' },
      { label: 'Notification Settings', to: '/settings/notifications' },
      { label: 'Notification Templates', to: '/settings/templates' },
      { label: 'Print Templates', to: '/settings/print-templates' },
      { label: 'Products', to: '/settings/products' },
      { label: 'Work Sources', to: '/settings/work-sources' },
      { label: 'Payment Modes', to: '/settings/payment-modes' },
      { label: 'Time Slots', to: '/settings/time-slots' },
      { label: 'Holidays', to: '/settings/holidays' },
      { label: 'Theme', to: '/settings/theme' },
      { label: 'Data Export', to: '/reports/collections/data-export' },
      { label: 'Communication Channels', to: '/settings/communication-channels' },
    ],
  },
  {
    group: 'Account',
    items: [
      { label: 'Profile', to: '/profile' },
      { label: 'Support', to: '/support' },
      { label: 'Activity Log', to: '/activity-log' },
    ],
  },
];

interface NavSearchProps {
  onClose: () => void;
}

export function NavSearch({ onClose }: NavSearchProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { groups, flat } = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filteredGroups = LINKS.map((g) => ({
      group: g.group,
      items: needle
        ? g.items.filter((item) => item.label.toLowerCase().includes(needle))
        : g.items,
    })).filter((g) => g.items.length > 0);
    const flatList = filteredGroups.flatMap((g) => g.items);
    return { groups: filteredGroups, flat: flatList };
  }, [query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function handleNavigate(to: string) {
    onClose();
    navigate(to);
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flat[activeIndex];
      if (item) handleNavigate(item.to);
    }
  }

  function handleBackdropClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  let flatIndex = -1;

  return (
    <div
      role="dialog"
      aria-label="Search navigation"
      aria-modal="true"
      className={styles.backdrop}
      onClick={handleBackdropClick}
    >
      <div className={styles.card}>
        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          placeholder="Search pages…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Search pages"
          aria-activedescendant={
            flat[activeIndex] ? `nav-search-result-${activeIndex}` : undefined
          }
        />
        <div className={styles.results} role="listbox" aria-label="Pages">
          {groups.length === 0 ? (
            <div className={styles.empty}>No results for &ldquo;{query}&rdquo;</div>
          ) : (
            groups.map((g) => (
              <div key={g.group} className={styles.group}>
                <div className={styles.groupHeading}>{g.group}</div>
                {g.items.map((item) => {
                  flatIndex += 1;
                  const i = flatIndex;
                  const isActive = i === activeIndex;
                  return (
                    <button
                      key={item.to}
                      id={`nav-search-result-${i}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`${styles.result} ${isActive ? styles.resultActive : ''}`}
                      onClick={() => handleNavigate(item.to)}
                      onMouseEnter={() => setActiveIndex(i)}
                    >
                      <span className={styles.resultLabel}>{item.label}</span>
                      <span className={styles.resultPath}>{item.to}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
